// src/components/themes/default/store/branch/BranchCheckoutView.tsx
"use client";

import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  ArrowLeft,
  Truck,
  MapPin,
  Tag,
  ShieldCheck,
  Loader2,
  Trash2,
  Plus,
  Minus,
  CheckCircle2,
  Package,
} from "lucide-react";
import { useBranchCart } from "./BranchCartContext";

interface BranchCheckoutViewProps {
  branch: {
    id: number;
    name: string;
    slug: string;
    contact_phone?: string;
    address?: string;
  };
  customerUser?: any;
  subdomain: string;
  primaryColor?: string;
  onBack: () => void;
  onOrderSuccess: (orderData: any) => void;
}

export default function BranchCheckoutView({
  branch,
  customerUser,
  subdomain,
  primaryColor = "#059669",
  onBack,
  onOrderSuccess,
}: BranchCheckoutViewProps) {
  const { cart, updateQuantity, removeFromCart, clearCart, cartTotal, totalItemCount } = useBranchCart();

  // ফর্ম ফিল্ড স্টেট (লগইন করা থাকলে স্বয়ংক্রিয়ভাবে ফিল হবে)
  const [fullName, setFullName] = useState(
    customerUser?.first_name || customerUser?.name || ""
  );
  const [phone, setPhone] = useState(customerUser?.phone || "");
  const [email, setEmail] = useState(customerUser?.email || "");
  const [streetAddress, setStreetAddress] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("dhaka");
  const [deliveryCharge, setDeliveryCharge] = useState(60);
  const [orderNotes, setOrderNotes] = useState("");

  // জিপিএস লোকেশন স্টেট
  const [gpsLocation, setGpsLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsStatusText, setGpsStatusText] = useState("");

  // কুপন স্টেট
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // রিজিয়ন পরিবর্তনে ডেলিভারি চার্জ আপডেট
  useEffect(() => {
    if (selectedRegion === "dhaka") {
      setDeliveryCharge(60);
    } else if (selectedRegion === "outside") {
      setDeliveryCharge(120);
    } else if (selectedRegion === "express") {
      setDeliveryCharge(150);
    }
  }, [selectedRegion]);

  // জিপিএস লাইভ লোকেশন ক্যাপচার
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      Swal.fire({
        position: "top",
        icon: "warning",
        title: "GPS সমর্থিত নয়",
        text: "আপনার ব্রাউজারে লোকেশন ট্র্যাকিং সুবিধা নেই।",
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });
      return;
    }

    setGpsLoading(true);
    setGpsStatusText("স্যাটেলাইটের সাথে সংযোগ হচ্ছে...");

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setGpsLoading(false);
        setGpsStatusText(`পিন করা হয়েছে: ${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
      },
      () => {
        setGpsLoading(false);
        setGpsStatusText("");
        Swal.fire({
          position: "top",
          icon: "error",
          title: "লোকেশন পাওয়া যায়নি",
          text: "অনুগ্রহ করে ব্রাউজারে লোকেশন পারমিশন দিন।",
          background: "#090d16",
          color: "#ffffff",
          customClass: { container: "!z-[999999]" },
        });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // কুপন কোড প্রয়োগ
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") || window.location.hostname === "127.0.0.1");

      const apiUrl = isLocal
        ? `http://${subdomain}.localhost:8000/api/v1/coupons/validate/`
        : `/api/v1/coupons/validate/`;

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ code: couponCode.trim(), subtotal: cartTotal }),
      });

      const data = await res.json();
      if (res.ok && data.valid) {
        setCouponDiscount(Number(data.discount_amount || 0));
        Swal.fire({
          position: "top",
          icon: "success",
          title: "কুপন সক্রিয় হয়েছে!",
          text: `আপনি ৳${data.discount_amount} ছাড় পেয়েছেন।`,
          showConfirmButton: false,
          timer: 2000,
          background: "#090d16",
          color: "#ffffff",
          customClass: { container: "!z-[999999]" },
        });
      } else {
        throw new Error(data.message || "কুপন কোডটি সঠিক নয়।");
      }
    } catch (err: any) {
      Swal.fire({
        position: "top",
        icon: "warning",
        title: "কুপন প্রযোজ্য নয়",
        text: err.message || "কুপন কোডটি কাজ করছে না।",
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });
    } finally {
      setCouponLoading(false);
    }
  };

  const totalPayable = Math.max(0, cartTotal + deliveryCharge - couponDiscount);

  // অর্ডার প্লেস করা
  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cart.length === 0) {
      Swal.fire({
        position: "top",
        icon: "warning",
        title: "কার্ট খালি",
        text: "অনুগ্রহ করে কার্টে পণ্য যোগ করুন।",
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });
      return;
    }

    if (!fullName.trim() || !phone.trim() || !streetAddress.trim()) {
      Swal.fire({
        position: "top",
        icon: "warning",
        title: "তথ্য অসম্পূর্ণ",
        text: "নাম, ফোন নম্বর এবং সম্পূর্ণ ঠিকানা দেওয়া আবশ্যক।",
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") || window.location.hostname === "127.0.0.1");

      const apiUrl = isLocal
        ? `http://${subdomain}.localhost:8000/api/v1/orders/`
        : `/api/v1/orders/`;

      const token =
        localStorage.getItem(`branch_customer_token_${branch.id}`) ||
        localStorage.getItem("customer_token") ||
        "";

      const payload = {
        branch_id: branch.id,
        customer_name: fullName.trim(),
        phone: phone.trim(),
        email: email.trim().toLowerCase(),
        shipping_address: `${streetAddress.trim()}, ${selectedRegion === "dhaka" ? "Dhaka City" : "Outside Dhaka"}`,
        shipping_method: selectedRegion === "dhaka" ? "Dhaka City" : selectedRegion === "express" ? "Express Delivery" : "Outside Dhaka",
        shipping_cost: deliveryCharge,
        discount: couponDiscount,
        coupon_code: couponDiscount > 0 ? couponCode : "",
        notes: orderNotes.trim(),
        latitude: gpsLocation?.lat || null,
        longitude: gpsLocation?.lng || null,
        payment_method: "cod",
        items: cart.map((i) => ({
          product_id: i.id,
          quantity: i.quantity,
          unit_price: i.price,
        })),
        total_amount: totalPayable,
      };

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Branch-ID": String(branch.id),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "অর্ডার সম্পন্ন করা যায়নি।");
      }

      clearCart();

      Swal.fire({
        position: "top",
        icon: "success",
        title: "অর্ডার সফল হয়েছে! 🎉",
        text: `অর্ডার #${data.order_number || data.id} সফলভাবে গ্রহণ করা হয়েছে। ${branch.name} আউটলেট থেকে দ্রুত ডেলিভারি নিশ্চিত করা হবে।`,
        confirmButtonText: "ঠিক আছে",
        confirmButtonColor: primaryColor,
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });

      onOrderSuccess(data);
    } catch (err: any) {
      Swal.fire({
        position: "top",
        icon: "error",
        title: "অর্ডার ব্যর্থ হয়েছে",
        text: err.message || "সার্ভারে যোগাযোগ করা যায়নি।",
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{ ["--branch-primary" as any]: primaryColor }}
      className="min-h-screen bg-[#f8fafc] text-slate-800 font-sans pb-16"
    >
      {/* টপ ন্যাভ বার */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Store</span>
          </button>

          <span className="text-xs font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-xl">
            {branch.name} Outlet Checkout
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900">
            Checkout & Order
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Complete your order with express verification from {branch.name}
          </p>
        </div>

        {/* ২-কলাম লেআউট */}
        <form onSubmit={handlePlaceOrder} className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* 🌟 বাম কলাম: Shipping & Delivery Details */}
          <div className="lg:col-span-7 bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-7 shadow-sm space-y-5">
            <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
              <Truck className="w-5 h-5 text-emerald-600" />
              <h2 className="text-base font-black text-slate-900">
                Shipping & Delivery Details
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Full Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. Najib Sk"
                  className="w-full px-3.5 py-2.5 rounded-2xl text-xs font-bold bg-slate-50 border border-slate-200 outline-none focus:border-emerald-500 focus:bg-white transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Phone Number <span className="text-rose-500">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="017xxxxxxxx"
                  className="w-full px-3.5 py-2.5 rounded-2xl text-xs font-mono font-bold bg-slate-50 border border-slate-200 outline-none focus:border-emerald-500 focus:bg-white transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Email Address <span className="text-rose-500">*</span>
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="najib@gmail.com"
                className="w-full px-3.5 py-2.5 rounded-2xl text-xs font-bold bg-slate-50 border border-slate-200 outline-none focus:border-emerald-500 focus:bg-white transition-all"
              />
            </div>

            {/* 🌟 লাইভ GPS লোকেশন বক্স */}
            <div className="p-4 rounded-2xl bg-emerald-50/60 border border-emerald-200/80 space-y-2.5">
              <div className="flex items-center gap-2 text-emerald-800 text-xs font-bold">
                <MapPin className="w-4 h-4 text-emerald-600" />
                <span>সঠিক ডেলিভারি লোকেশন (GPS)</span>
              </div>
              <p className="text-[11px] text-emerald-700 leading-relaxed">
                আপনার বর্তমান জিপিএস লোকেশন শেয়ার করুন যাতে ডেলিভারি ম্যান সরাসরি আপনার ঠিকানায় পৌঁছাতে পারে।
              </p>

              <button
                type="button"
                onClick={handleGetLocation}
                disabled={gpsLoading}
                className="w-full py-2.5 px-4 rounded-xl border border-emerald-400 bg-white hover:bg-emerald-100/50 text-emerald-800 text-xs font-black transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs active:scale-95"
              >
                {gpsLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                ) : (
                  <MapPin className="w-4 h-4 text-emerald-600" />
                )}
                <span>{gpsLocation ? "✓ লোকেশন পিন করা হয়েছে" : "আমার বর্তমান লাইভ লোকেশন পিন করুন"}</span>
              </button>

              {gpsStatusText && (
                <span className="block text-[10px] font-mono text-emerald-700 font-bold text-center">
                  {gpsStatusText}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  City / Region <span className="text-rose-500">*</span>
                </label>
                <select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-2xl text-xs font-bold bg-slate-50 border border-slate-200 outline-none focus:border-emerald-500 focus:bg-white transition-all cursor-pointer"
                >
                  <option value="dhaka">Dhaka City (৳60 Delivery)</option>
                  <option value="outside">Outside Dhaka / Khulna (৳120 Delivery)</option>
                  <option value="express">Express Outlet Delivery (৳150)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Full Street Address <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={streetAddress}
                  onChange={(e) => setStreetAddress(e.target.value)}
                  placeholder="House, Road, Area details"
                  className="w-full px-3.5 py-2.5 rounded-2xl text-xs font-bold bg-slate-50 border border-slate-200 outline-none focus:border-emerald-500 focus:bg-white transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Order Notes (Optional)
              </label>
              <textarea
                rows={2}
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="Notes about your delivery instructions..."
                className="w-full p-3 rounded-2xl text-xs font-medium bg-slate-50 border border-slate-200 outline-none focus:border-emerald-500 focus:bg-white transition-all resize-none"
              />
            </div>
          </div>

          {/* 🌟 ডান কলাম: Order Summary */}
          <div className="lg:col-span-5 bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-7 shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h2 className="text-base font-black text-slate-900">Order Summary</h2>
              <span className="text-xs font-bold text-slate-400 font-mono">
                {totalItemCount} Items
              </span>
            </div>

            {/* প্রোডাক্ট তালিকা */}
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="p-3 rounded-2xl border border-slate-100 bg-slate-50/50 flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-5 h-5 text-slate-300" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-black text-slate-900 truncate">{item.name}</h4>
                      <p className="text-xs font-mono font-bold text-emerald-600 mt-0.5">
                        ৳{item.price.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg p-0.5">
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.id, -1)}
                        className="p-1 hover:bg-slate-100 rounded text-slate-600"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-xs font-bold font-mono px-1">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.id, 1)}
                        className="p-1 hover:bg-slate-100 rounded text-slate-600"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeFromCart(item.id)}
                      className="p-1 text-slate-400 hover:text-rose-500 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* কুপন ইনপুট */}
            <div className="pt-2 border-t border-slate-100">
              <label className="block text-[11px] font-bold text-slate-600 mb-1.5 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-rose-500" />
                <span>Have a Coupon?</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="E.G. EID28"
                  className="flex-1 px-3.5 py-2 rounded-xl text-xs font-mono font-bold uppercase bg-slate-50 border border-slate-200 outline-none focus:border-emerald-500"
                />
                <button
                  type="button"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading}
                  style={{ backgroundColor: primaryColor }}
                  className="px-4 py-2 rounded-xl text-xs font-black text-white hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-50"
                >
                  {couponLoading ? "..." : "Apply"}
                </button>
              </div>
            </div>

            {/* বিলিং সামারি */}
            <div className="space-y-2 pt-2 border-t border-slate-100 text-xs">
              <div className="flex items-center justify-between text-slate-600">
                <span>Subtotal</span>
                <span className="font-mono font-bold">৳{cartTotal.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-slate-600">
                <span>Delivery Charge</span>
                <span className="font-mono font-bold">৳{deliveryCharge}</span>
              </div>
              {couponDiscount > 0 && (
                <div className="flex items-center justify-between text-rose-600 font-bold">
                  <span>Coupon Discount</span>
                  <span className="font-mono">-৳{couponDiscount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm font-black text-slate-900 pt-2 border-t border-slate-200">
                <span>Total Payable</span>
                <span className="text-lg font-mono text-emerald-600">
                  ৳{totalPayable.toLocaleString()}
                </span>
              </div>
            </div>

            {/* বড় প্লেস অর্ডার বাটন */}
            <button
              type="submit"
              disabled={isSubmitting || cart.length === 0}
              style={{ backgroundColor: primaryColor }}
              className="w-full py-3.5 rounded-2xl text-xs font-black text-white shadow-xl flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95 disabled:opacity-50 hover:opacity-95"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Placing Order...</span>
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  <span>Place Order (৳{totalPayable.toLocaleString()})</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}